import React from 'react';
import HelpLink from '@sls-react/components/Links/HelpLink';
import TermsLink from '@sls-react/components/Links/TermsLink';
import useMobile from '@sls-react/hooks/useMobile';
import SocialLink from '@sls-react/components/Links/SocialLink';
import GetTheAppFooter from '@sls-react/components/Chrome/GetTheAppFooter';
import BlogLink from '@sls-react/components/Links/BlogLink';
import Image from '@sls-react/components/Image';
import IosAppStoreLink from '@sls-react/components/Links/IosAppStoreLink';
import PlayStoreIcon from '@sls-react/static/svg/PlayStoreIcon';
import GooglePlayStoreLink from '@sls-react/components/Links/GooglePlayStoreLink';
import { clickedAppCta } from '@sls-react/services/analytics/events/generic';
import AboutUsLink from '@sls-react/components/Links/AboutUsLink';
import TrustpilotFooter from './TrustpilotFooter';
import LazyLoad from 'react-lazyload';
import LogoTagline from '@sls-react/components/Chrome/LogoTagline';
import {
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  YoutubeIcon,
} from '@sls-react/static/svg/SocialIcons';
import SellNowLink from '@sls-react/components/Links/SellNowLink';
import ShopLink from '@sls-react/components/Links/ShopLink';
import ValueGuideLink from '@sls-react/components/Links/ValueGuideLink';
import FooterEmailCapture from '@sls-react/components/Chrome/FooterEmailCapture';
import IOS_APP_STORE from '@sls-react/static/ios-app-store.png';

const ListSection: React.FC<{ title: string }> = ({ children, title }) => (
  <ul className="space-y-3">
    <li className="text-sm font-semibold uppercase text-slate-green-500">
      {title}
    </li>
    {children}
  </ul>
);

const Footer: React.FC<{ excludeAppCta?: boolean }> = ({ excludeAppCta }) => {
  const mobile = useMobile();

  return (
    <footer className="py-8 bg-turf-green-500 text-pastel-green-500 md:py-12">
      <div className="container">
        <div className="grid grid-flow-row md:grid-flow-col auto-cols-auto gap-6">
          <div className="max-w-sm text-center md:text-left">
            <LogoTagline className="mx-auto mb-2 md:mx-0 md:mb-6" />

            <p className="mb-4 leading-loose md:mb-6">
              We&apos;re proud to be making sports more affordable, while
              keeping gear out of landfills.{' '}
              <AboutUsLink className="underline">Learn more</AboutUsLink>
            </p>

            <div className="flex items-center justify-center md:justify-start space-x-4 text-slate-green-500">
              <SocialLink link="instagram" title="Instagram">
                <InstagramIcon />
              </SocialLink>

              <SocialLink link="facebook" title="Facebook">
                <FacebookIcon />
              </SocialLink>

              <SocialLink link="youtube" title="YouTube">
                <YoutubeIcon />
              </SocialLink>

              <SocialLink link="twitter" title="Twitter">
                <TwitterIcon />
              </SocialLink>

              <BlogLink title="SidelineSwap's Blog">Blog</BlogLink>
            </div>
          </div>

          <ListSection title="Customer Support">
            <li>
              <HelpLink link="shipping">Shipping FAQ</HelpLink>
            </li>
            <li>
              <HelpLink link="refundPolicy">Refunds</HelpLink>
            </li>
            <li>
              <HelpLink link="buyerProtection">Buyer Protection</HelpLink>
            </li>
            <li>
              <HelpLink link="default">Help Center</HelpLink>
            </li>
          </ListSection>

          <ListSection title="Sell on SidelineSwap">
            <li>
              <ValueGuideLink>Value Guide</ValueGuideLink>
            </li>

            <li>
              <SellNowLink>How to Sell</SellNowLink>
            </li>

            <li>
              <ShopLink type="nav" path="how-to-ship/l163448">
                How to Ship
              </ShopLink>
            </li>

            <li>
              <HelpLink link="ims">Seller Integrations</HelpLink>
            </li>

            <li>
              <BlogLink page="/tag/selling/">More Seller Resources</BlogLink>
            </li>
          </ListSection>

          <ListSection title="Company">
            <li>
              <AboutUsLink>About Us</AboutUsLink>
            </li>
            <li>
              <HelpLink link="affiliates">Affiliates</HelpLink>
            </li>
            <li>
              <TermsLink type="terms">Terms</TermsLink>
            </li>
            <li>
              <TermsLink type="privacy">Privacy</TermsLink>
            </li>
            <li>
              <TermsLink type="accessibility">Accessibility</TermsLink>
            </li>
            <li>
              <HelpLink link="press">Press</HelpLink>
            </li>
            <li>
              <a href="/sitemap/index">Sitemap</a>
            </li>
          </ListSection>

          {mobile ? (
            <GetTheAppFooter className="font-semibold text-slate-green-500" />
          ) : (
            <ListSection title="Get the App">
              <li>
                <IosAppStoreLink
                  source="footer"
                  className="mr-2"
                  onClick={() => clickedAppCta('ios', 'footer')}
                >
                  <Image
                    className="inline-block md:mr-8"
                    height={35}
                    width={118}
                    src={IOS_APP_STORE}
                    alt="iOS App Store"
                  />
                </IosAppStoreLink>
              </li>

              <li>
                <GooglePlayStoreLink
                  onClick={() => clickedAppCta('android', 'footer')}
                >
                  <PlayStoreIcon height={35} width={120} />
                </GooglePlayStoreLink>
              </li>
            </ListSection>
          )}
        </div>

        <LazyLoad offset={200}>
          <TrustpilotFooter />
        </LazyLoad>

        <hr className="my-8" />

        <FooterEmailCapture />

        <div className="pt-12 text-center text-slate-green-500">
          SidelineSwap, Inc.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
